import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';

// SearchPill — global sökruta som lever i top-nav på alla huvudsidor
// (Hub, Gallery, SetView). Klick eller Cmd+K → /search.
//
// På desktop (≥md) renderas pillen som en full inline-input med
// placeholder och ⌘K-hint. På mobil (<md) renderas bara lupp-ikonen
// för att spara plats åt sid-titeln.
//
// Olika sidor använder olika "size":
//   - "full"    Hub-storlek: stort hit-target, max-bredd 720px (default).
//   - "compact" Gallery/SetView-storlek: passar i 58px-tall top-nav.

interface SearchPillProps {
  /** Placeholder-text på desktop. Default: "Search shoots, sets, tags…" */
  placeholder?: string;
  /** Visual storlek — "compact" för 58px-nav, "full" för Hub-strip. */
  size?: 'compact' | 'full';
  /** Extra utility-klasser (för att t.ex. styra max-bredd från utsidan). */
  className?: string;
}

export function SearchPill({
  placeholder = 'Search shoots, sets, tags…',
  size = 'compact',
  className = '',
}: SearchPillProps) {
  const navigate = useNavigate();

  // Höjder hålls i synk med Tailwind min-h-* + py-* utan att försöka
  // härleda från Tailwind dynamiskt (det funkar inte på purge-tid).
  const heightClasses =
    size === 'full'
      ? 'py-3 min-h-[44px]'
      : 'py-2 min-h-[36px]';

  const textSizeClass = size === 'full' ? 'text-sm' : 'text-[13px]';

  return (
    <>
      {/* Desktop (≥md): full input-look */}
      <button
        type="button"
        onClick={() => navigate('/search')}
        className={[
          'hidden md:block relative text-left group min-w-0',
          size === 'full' ? 'flex-1 max-w-[720px]' : 'w-full max-w-[440px]',
          className,
        ].join(' ')}
        aria-label="Search"
      >
        <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-loupe-text-3 pointer-events-none" />
        <div
          className={[
            'w-full pl-10 pr-12 bg-loupe-bg-2 border border-loupe-border rounded-xl',
            'text-loupe-text-3 flex items-center',
            'group-hover:border-loupe-border-2 transition-colors',
            heightClasses,
            textSizeClass,
          ].join(' ')}
        >
          <span className="truncate">{placeholder}</span>
        </div>
        <span className="hidden lg:inline absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 bg-loupe-bg-3 border border-loupe-border-2 text-loupe-text-3 text-[11px] font-mono rounded pointer-events-none">
          ⌘K
        </span>
      </button>

      {/* Mobil (<md): bara ikon-knapp */}
      <button
        type="button"
        onClick={() => navigate('/search')}
        className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-loupe-text-2 hover:text-white hover:bg-loupe-bg-2 transition-colors"
        aria-label="Search"
      >
        <SearchIcon className="w-4 h-4" />
      </button>
    </>
  );
}
